/**
 * Confidence Rating System - Business Logic
 *
 * Calculates confidence ratings for business valuations based on:
 * - Revenue growth data quality
 * - Expense data quality
 * - Normalization adjustments
 */

// Default weights for confidence factors (can be overridden by admin settings)
export const DEFAULT_CONFIDENCE_WEIGHTS = {
  revenue: 1,
  expenses: 1,
  normalization: 1,
};

// Revenue growth confidence factors
export const revenueGrowthFactors = {
  noGrowth: {
    id: 'revenue_no_growth',
    category: 'revenue',
    level: 'low',
    title: 'No Revenue Growth Set',
    description: 'Using 0% growth with no manual or historical values reduces confidence in projections.',
  },
  manualGrowth: {
    id: 'revenue_manual_growth',
    category: 'revenue',
    level: 'medium',
    title: 'Manual Growth Rate',
    description: 'Manual growth rate provides better projections than no growth assumption.',
  },
  incompleteHistorical: {
    id: 'revenue_incomplete_historical',
    category: 'revenue',
    level: 'medium',
    title: 'Incomplete Historical Revenue',
    description: 'Some historical data available but not complete for all periods.',
  },
  historicalData: {
    id: 'revenue_historical_data',
    category: 'revenue',
    level: 'high',
    title: 'Historical Revenue Data',
    description: 'Complete historical revenue data provides the most reliable growth projections.',
  },
};

// Expenses confidence factors
export const expensesFactors = {
  noExpenses: {
    id: 'expenses_zero',
    category: 'expenses',
    level: 'low',
    title: 'No Expenses Set',
    description: 'Using $0 expenses significantly reduces valuation accuracy.',
  },
  benchmark: {
    id: 'expenses_benchmark',
    category: 'expenses',
    level: 'medium',
    title: 'Benchmark Expenses',
    description: 'Using industry benchmark expenses provides reasonable estimates.',
  },
  categorized: {
    id: 'expenses_categorized',
    category: 'expenses',
    level: 'high',
    title: 'Categorized Expenses',
    description: 'Detailed category-level expenses provide the most accurate cost structure.',
  },
};

// Normalization confidence factors
export const normalizationFactors = {
  noNormalization: {
    id: 'normalization_none',
    category: 'normalization',
    level: 'low',
    title: 'No Normalization',
    description: 'Only categorized expenses without adjustments may not reflect true earning power.',
  },
  withAdjustments: {
    id: 'normalization_adjustments',
    category: 'normalization',
    level: 'high',
    title: 'Normalized with Adjustments',
    description: 'Add-backs and adjustments to individual categories provide the most accurate normalized EBITDA.',
  },
};

/**
 * Determine revenue growth factor from line inputs
 * @param {Object} lineInputs - Object containing line-specific inputs (wealth, life, pc, group)
 * @returns {Object} The appropriate revenue growth factor
 */
export function getRevenueGrowthFactor(lineInputs) {
  // Check all lines for historical data
  const lines = ['wealth', 'life', 'pc', 'group'];
  let hasAnyHistory = false;
  let hasCompleteHistory = false;
  let hasManualGrowth = false;

  for (const lineId of lines) {
    const lineData = lineInputs?.[lineId];
    if (!lineData) continue;

    // Check historical values (y1-y5)
    const history = lineData.history || {};
    const historyValues = [history.y1, history.y2, history.y3, history.y4, history.y5]
      .filter((v) => v !== undefined && v !== null && v !== '' && Number(v) > 0);

    if (historyValues.length >= 5) {
      hasCompleteHistory = true;
    } else if (historyValues.length >= 2) {
      hasAnyHistory = true;
    }

    // Check manual growth rate
    const growth = Number(lineData.growth || 0);
    if (growth !== 0) {
      hasManualGrowth = true;
    }
  }

  // Prioritize: complete historical > incomplete historical > manual growth > no growth
  if (hasCompleteHistory) {
    return revenueGrowthFactors.historicalData;
  }
  if (hasAnyHistory) {
    return revenueGrowthFactors.incompleteHistorical;
  }
  if (hasManualGrowth) {
    return revenueGrowthFactors.manualGrowth;
  }
  return revenueGrowthFactors.noGrowth;
}

/**
 * Determine expenses factor from expenses inputs
 * @param {Object} expensesInputs - Expenses configuration
 * @returns {Object} The appropriate expenses factor
 */
export function getExpensesFactor(expensesInputs) {
  const useBenchmark = !!expensesInputs?.useBenchmark;

  // Check for categorized expenses
  const categories = expensesInputs?.operatingExpenseCategories || [];
  const hasCategorizedExpenses = categories.some((cat) => {
    const amount = Number(cat?.amount || 0);
    return amount > 0;
  });

  // Calculate total expenses
  const totalOperatingExpenses = Number(expensesInputs?.operatingExpenses || 0);
  const categoryTotal = categories.reduce((sum, cat) => sum + Number(cat?.amount || 0), 0);
  const totalExpenses = Math.max(totalOperatingExpenses, categoryTotal);

  // Prioritize: categorized > benchmark > no expenses
  if (hasCategorizedExpenses) {
    return expensesFactors.categorized;
  }
  if (useBenchmark && totalExpenses > 0) {
    return expensesFactors.benchmark;
  }
  if (totalExpenses === 0) {
    return expensesFactors.noExpenses;
  }
  // Has some expenses but not categorized and not benchmark
  return expensesFactors.benchmark;
}

/**
 * Determine normalization factor from adjustments
 * @param {Array} normalizingAdjustments - Array of normalizing adjustment objects
 * @param {Object} expensesInputs - Expenses configuration (for category-level adjustments)
 * @returns {Object} The appropriate normalization factor
 */
export function getNormalizationFactor(normalizingAdjustments, expensesInputs) {
  // Check for top-level normalizing adjustments
  const hasNormalizingAdjustments =
    Array.isArray(normalizingAdjustments) &&
    normalizingAdjustments.some((adj) => Number(adj?.amount || 0) !== 0);

  // Check for category-level adjustments in expense categories
  const categories = expensesInputs?.operatingExpenseCategories || [];
  const hasCategoryAdjustments = categories.some((cat) => {
    const adjustments = cat?.adjustments || [];
    return adjustments.some((adj) => Number(adj?.amount || 0) !== 0);
  });

  if (hasNormalizingAdjustments || hasCategoryAdjustments) {
    return normalizationFactors.withAdjustments;
  }
  return normalizationFactors.noNormalization;
}

/**
 * Gather all confidence factors from valuation inputs
 * @param {Object} lineInputs - Line-specific inputs
 * @param {Object} expensesInputs - Expenses configuration
 * @param {Array} normalizingAdjustments - Normalizing adjustments
 * @returns {Array} Array of confidence factors
 */
export function gatherConfidenceFactors(lineInputs, expensesInputs, normalizingAdjustments) {
  const factors = [];

  // Revenue factor
  factors.push(getRevenueGrowthFactor(lineInputs));

  // Expenses factor
  factors.push(getExpensesFactor(expensesInputs));

  // Normalization factor
  factors.push(getNormalizationFactor(normalizingAdjustments, expensesInputs));

  return factors;
}

/**
 * Calculate confidence score from factors
 * @param {Array} factors - Array of confidence factors
 * @param {Object} [weights] - Optional custom weights by category (revenue, expenses, normalization)
 * @returns {Object} Confidence rating with overall level, score, factors, and recommendations
 */
export function calculateConfidence(factors, weights = null) {
  if (!factors || factors.length === 0) {
    return {
      overall: 'low',
      score: 0,
      factors: [],
      recommendations: ['Add revenue, expense, and normalization data to improve confidence.'],
    };
  }

  // Use provided weights or defaults (balanced/equal weighting)
  const categoryWeights = weights || DEFAULT_CONFIDENCE_WEIGHTS;

  const levelScores = {
    low: 0,
    medium: 50,
    high: 100,
  };

  let totalWeight = 0;
  let weightedScore = 0;

  factors.forEach((factor) => {
    // Get weight by category (default to 1 for balanced weighting)
    const weight = categoryWeights[factor.category] ?? 1;
    const score = levelScores[factor.level] || 0;
    totalWeight += weight;
    weightedScore += score * weight;
  });

  const finalScore = totalWeight > 0 ? Math.round(weightedScore / totalWeight) : 0;

  // Thresholds adjusted so that:
  // - 2 Medium + 1 Low (33%) = Medium
  // - 2 High + 1 Low (67%) = Medium  
  // - 2 High + 1 Medium (83%) = High
  // - All High (100%) = High
  let overall;
  if (finalScore >= 80) {
    overall = 'high';
  } else if (finalScore >= 25) {
    overall = 'medium';
  } else {
    overall = 'low';
  }

  const recommendations = generateRecommendations(factors);

  return {
    overall,
    score: finalScore,
    factors,
    recommendations,
  };
}

/**
 * Generate recommendations based on factors
 * @param {Array} factors - Array of confidence factors
 * @returns {Array} Array of recommendation strings
 */
export function generateRecommendations(factors) {
  const recommendations = [];
  const lowFactors = factors.filter((f) => f.level === 'low');
  const mediumFactors = factors.filter((f) => f.level === 'medium');

  // Critical issues first (low factors)
  if (lowFactors.some((f) => f.category === 'revenue')) {
    recommendations.push(
      'Add historical revenue data or set manual growth rates to improve projections.'
    );
  }

  if (lowFactors.some((f) => f.category === 'expenses')) {
    recommendations.push('Input detailed expense categories or use benchmark expenses.');
  }

  if (lowFactors.some((f) => f.category === 'normalization')) {
    recommendations.push('Add normalizing adjustments to reflect true earning power.');
  }

  // Improvement opportunities (medium factors)
  if (mediumFactors.some((f) => f.id === 'revenue_manual_growth')) {
    recommendations.push(
      'Consider using historical revenue data for more accurate growth projections.'
    );
  }

  if (mediumFactors.some((f) => f.id === 'expenses_benchmark')) {
    recommendations.push(
      'Replace benchmark expenses with actual categorized expenses for better accuracy.'
    );
  }

  return recommendations;
}

/**
 * Check if validation is recent (within 90 days)
 * @param {string} validatedAt - ISO date string of validation
 * @returns {boolean} Whether validation is recent
 */
export function isValidationRecent(validatedAt) {
  if (!validatedAt) return false;
  const validationDate = new Date(validatedAt);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - validationDate.getTime()) / (1000 * 60 * 60 * 24));
  return daysDiff <= 90;
}

/**
 * Format validation date for display
 * @param {string} validatedAt - ISO date string of validation
 * @returns {string} Formatted date string
 */
export function formatValidationDate(validatedAt) {
  if (!validatedAt) return '';
  const date = new Date(validatedAt);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
